import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { ROUTES } from "../../routes/routes.constants";
import { getPostLoginPath } from "../../utils/postLoginPath";
import { logout, otpLoginSuccess, selectAuth, selectToken } from "../../redux/authSlice";
import "../../App.css";
import { ApiCall } from "../../helper/axios";
import CustomLoader from '../../common_components/CustomLoader';
import OtpInput from 'react-otp-input';
import * as Yup from "yup";
import AuthErrorBanner from "./AuthErrorBanner";
import AppButton from "../../common_components/AppButton";
const validationSchema = Yup.object().shape({
    otp: Yup.string()
        .required("Verification code is required.")
        .matches(/^[0-9]{6}$/, "Verification code must be 6 digits.")
});
// Timer duration in seconds (5 minutes = 300 seconds) - Easy to change
const TIMER_DURATION_SECONDS = 300; // 5 minutes
const TIMER_STORAGE_KEY = 'otp_timer_start';
export default function Verify() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [apiError, setApiError] = useState(null);
    const [remainingSeconds, setRemainingSeconds] = useState(0);
    const [showResendButton, setShowResendButton] = useState(false);
    const timerIntervalRef = useRef(null);
    const fromPath = location.state?.from || ROUTES.home.path;
    const token = useSelector(selectToken);
    const userDetail = useSelector(selectAuth);
    // Format seconds to MM:SS
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };
    // Start or resume timer
    const startTimer = () => {
        const storedStartTime = localStorage.getItem(TIMER_STORAGE_KEY);
        const now = Date.now();
        let elapsedSeconds = 0;
        if (storedStartTime) {
            // Timer was already started, calculate elapsed time
            elapsedSeconds = Math.floor((now - parseInt(storedStartTime)) / 1000);
        }
        else {
            // First time, store start time
            localStorage.setItem(TIMER_STORAGE_KEY, now.toString());
        }
        const remaining = Math.max(0, TIMER_DURATION_SECONDS - elapsedSeconds);
        setRemainingSeconds(remaining);
        setShowResendButton(remaining === 0);
        // Clear existing interval if any
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }
        // Start countdown
        timerIntervalRef.current = setInterval(() => {
            setRemainingSeconds((prev) => {
                if (prev <= 1) {
                    setShowResendButton(true);
                    if (timerIntervalRef.current) {
                        clearInterval(timerIntervalRef.current);
                        timerIntervalRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };
    // Reset timer
    const resetTimer = () => {
        localStorage.removeItem(TIMER_STORAGE_KEY);
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
        }
        setRemainingSeconds(0);
        setShowResendButton(false);
    };
    // Initialize timer on mount
    useEffect(() => {
        startTimer();
        // Cleanup on unmount
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, []); // Run only once on mount
    // Handle Resend OTP
    const handleResendOTP = async () => {
        try {
            const payload = {
                temp_token: userDetail?.token,
                entity_type: userDetail?.otpEntityType,
                user_id: userDetail?.otpUserId
            };
            // Reset timer and start again
            await ApiCall('POST', '/api/auth/resend-otp', payload, { Authorization: token });
            resetTimer();
            startTimer();
        }
        catch (error) {
            console.error('error', error);
        }
    };
    if (!token) {
        return _jsx(Navigate, { to: ROUTES.login.path, replace: true });
    }
    const formik = useFormik({
        initialValues: {
            otp: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            if (values?.otp?.length === 6) {
                try {
                    const payload = {
                        temp_token: token,
                        otp: values?.otp
                    };
                    const res = await ApiCall("POST", `/api/auth/verify-otp`, payload, { authentication: token });
                    if (res?.data?.success && res?.status === 200) {
                        const result = res?.data?.data;
                        setApiError(null);
                        dispatch(otpLoginSuccess({
                            token: result?.token,
                            user: result?.user,
                            permissions: result?.permissions,
                            entity_type: result?.entity_type,
                        }));
                        // Decide redirection: no deep-link → never land on the internal Home showcase
                        // page. Prefer the user's last_visited_page from DB, else the default module.
                        let finalPath = fromPath;
                        if (fromPath === ROUTES.home.path || fromPath === ROUTES.verify.path || fromPath === ROUTES.login.path || fromPath === ROUTES.root.path) {
                            finalPath = getPostLoginPath(result);
                        }
                        navigate(finalPath, { replace: true });
                    }
                    else {
                        const msg = res?.response?.data?.message || 'Something went wrong';
                        setApiError({ title: msg, description: "Please try again." });
                        formik.setSubmitting(false);
                    }
                }
                catch (error) {
                    setApiError({ title: 'Verification failed. Please try again.', description: "" });
                    formik.setSubmitting(false);
                }
            }
            else {
                formik.setSubmitting(false);
            }
        },
    });
    const handleBackLogin = () => {
        // Reset timer when going back to login
        resetTimer();
        // Clear OTP-pending auth state, otherwise AppRoutes will keep redirecting back to /verify
        dispatch(logout());
        navigate(ROUTES.login.path, { replace: true });
    };
    const handleChange = (value) => {
        formik.setFieldValue("otp", value);
        if (apiError)
            setApiError(null);
    };
    return (_jsxs(Box, { className: "login-page-container", children: [_jsx(Box, { className: "login-logo-container", sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: _jsx("img", { alt: "GROUP 8A Logo", src: "/assets/loginImage/logo-final.svg", className: "login-logo-img" }) }), _jsx(Paper, { elevation: 0, className: "login-form-card", children: _jsx(FormikProvider, { value: formik, children: _jsxs(Box, { component: "form", onSubmit: formik.handleSubmit, className: "login-form", children: [_jsxs(Box, { className: "login-header", children: [_jsx(Typography, { variant: "h4", className: "login-title", children: "Two-Factor Verification" }), _jsx(Typography, { variant: "body1", className: "login-subtitle", children: "Enter the 6-digit code sent to your email." })] }), _jsxs(Box, { className: "login-form-fields", children: [_jsxs(Box, { className: "login-form-inputs-wrapper", children: [apiError && (_jsx(Box, { sx: { paddingBottom: '15px', width: '100%' }, children: _jsx(AuthErrorBanner, { message: apiError }) })), _jsx(Box, { className: "login-form-inputs", sx: { paddingBottom: '15px', width: '100%' }, children: _jsx("p", { className: "text-otp-verify", style: { margin: '0' }, children: "Enter your 6-digit security code." }) }), _jsxs(Box, { className: "login-form-inputs", sx: { paddingBottom: '0', width: '100%' }, children: [_jsx(OtpInput, { value: formik.values.otp, onChange: (value) => handleChange(value), numInputs: 6, containerStyle: { width: "100%" }, renderInput: (props) => (_jsx("div", { className: "otp-input-sec", children: _jsx("input", { ...props, onBlur: () => formik.setFieldTouched("otp", true) }) })) }), _jsxs("div", { className: "d-flex align-items-end justify-content-between gap-1 w-100", children: [_jsx("p", { className: "otp-verify-msg-txt", children: "We sent a 6-digit verification code to your registered email address." }), showResendButton ? (_jsx(AppButton, { children: "Resend Code", onClick: handleResendOTP, className: "login-forgot-password-link line-height-normal" })) : (_jsx(Typography, { sx: {
                                                                    fontSize: '14px',
                                                                    color: '#71717a',
                                                                    fontWeight: 400,
                                                                }, children: _jsx("span", { style: { fontWeight: 400, color: '#FF64BD' }, children: formatTime(remainingSeconds) }) }))] }), formik.touched.otp && formik.errors.otp && (_jsx("p", { className: "error-field", children: formik.errors.otp }))] })] }), _jsx(Box, { className: "login-signin-button-container", children: _jsx(AppButton, { type: "submit", fullWidth: true, className: "login-signin-button", variant: "contained", color: "primary", disabled: formik.isSubmitting, children: formik.isSubmitting ? _jsx(CustomLoader, { sx: { color: 'white' } }) : 'Verify & Login' }) }), _jsx(Box, { className: "login-forgot-password-container back-to-login", children: _jsx(AppButton, { children: "Back to Login", onClick: () => handleBackLogin(), className: "login-forgot-password-link" }) })] })] }) }) }), _jsx("div", { className: "bird-container bird-container--one", children: _jsx("div", { className: "bird bird--one", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--two", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--three", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) }), _jsx("div", { className: "bird-container bird-container--four", children: _jsx("div", { className: "bird bird--two", children: "\u00A0" }) })] }));
}
